// Social Feed Page
// Main page for the TikTok-inspired social feed with mobile bottom navigation

import React, { useEffect, useState } from "react";
import { useAuthContext } from "../../providers";
import { MobileFeedWrapper } from "../../components/SocialFeed/MobileFeedWrapper";
import { logger } from "../../utils/logger";
import { sessionRestore } from "../../services/Authentication/sessionRestore";
import { config } from "../../config/env";
import { authInterceptor } from "../../services/Authentication/authInterceptor";
import { secureStorage } from "../../services/Storage/secureStorage";

export const SocialFeed: React.FC = () => {
  // useAuthContext, NOT useAuth: useAuth is a plain hook holding its own
  // useState, so every direct caller spins up an independent copy of the auth
  // state and runs its own async session check. AuthProvider already owns one
  // instance for the whole tree; calling useAuth() here created a second that
  // resolved on its own schedule, and MobileFeedWrapper a third.
  const { user, loading, checkAuth } = useAuthContext();

  // Session restore below is async, so there is a window where the provider has
  // finished (loading === false) but the stored session has not been read back
  // yet. Rendering the "please log in" branch during that window flashes a
  // logged-out message at a user who is logged in. Stay on the loading state
  // until a restore attempt has actually settled.
  const [isRestoringSession, setIsRestoringSession] = useState(true);

  // Ensure session is restored when page loads (especially after onboarding redirect)
  useEffect(() => {
    // Wait for the auth provider's own check to finish before deciding whether
    // a restore is even needed.
    if (loading) return;

    let cancelled = false;
    const ensureSession = async () => {
      // Mock credentials mode - restore from stored tokens
      if (config.dev.mockCredentials) {
        const token = authInterceptor.getAccessToken();
        const storedUser = secureStorage.getUserData();
        
        if (token && storedUser) {
          // Verify it's a mock token (JWT-like format with 3 parts)
          const tokenParts = token.split('.');
          if (tokenParts.length === 3) {
            try {
              const payload = JSON.parse(atob(tokenParts[1]));
              if (payload.sub && payload.email) {
                console.log('🔓 Mock credentials mode: Session restored on social feed');
                // User data is already stored, checkAuth will pick it up
                await checkAuth();
                return;
              }
            } catch (e) {
              // Not a valid JWT format
            }
          }
        }
        // If no valid mock tokens, still allow access in mock mode
        console.log('🔓 Mock credentials mode: No stored tokens, but allowing access');
        return;
      }
      
      // Normal mode - restore session
      if (!user) {
        // Try to restore session if user is not authenticated
        const sessionRestored = await sessionRestore.restoreSession();
        if (sessionRestored) {
          // Session restored, check auth to get user data
          await checkAuth();
        }
      }
    };

    ensureSession()
      .finally(() => {
        if (!cancelled) setIsRestoringSession(false);
      });

    return () => { cancelled = true; };
  }, [user, loading, checkAuth]);

  if (loading || isRestoringSession) {
    return (
      <div style={{ 
        display: "flex", 
        alignItems: "center", 
        justifyContent: "center", 
        height: "100vh",
        color: "white"
      }}>
        Loading...
      </div>
    );
  }

  // In mock credentials mode, allow access even without user (user can explore)
  if (!user && !config.dev.mockCredentials) {
    return (
      <div style={{ 
        display: "flex", 
        alignItems: "center", 
        justifyContent: "center", 
        height: "100vh",
        color: "white"
      }}>
        Please log in to view your social feed.
      </div>
    );
  }
  
  // In mock mode without user, use a default user ID
  const userId = user?.id || 'mock-user-demo';

  return (
    <MobileFeedWrapper
      userId={userId}
      onItemInteraction={(interaction) => {
        logger.log("Feed interaction:", interaction);
      }}
      onSwipeModeToggle={(enabled) => {
        logger.log("Swipe mode toggled:", enabled);
      }}
    />
  );
};
